import { useCallback, useEffect, useRef } from "react";

/** Drift speed. Slow enough to read a card name as it passes. */
const PIXELS_PER_SECOND = 60;

/** Longest gap a single frame may account for — roughly three frames. */
const MAX_FRAME_MS = 50;

type MarqueeScrollOptions = {
  /** Arabic drifts the other way, and its `scrollLeft` runs from 0 downwards. */
  rtl: boolean;
  /** Changing this restarts the drift from the head of the list. */
  resetKey?: unknown;
};

type Drag = {
  pointerId: number;
  startX: number;
  startScroll: number;
};

/**
 * The distance from an item to its duplicate — one full pass of the list.
 * Measured from the DOM rather than derived from `scrollWidth / 2`, which is
 * half a gap short: the two halves have a gap between them that the arithmetic
 * split would put inside one of them.
 */
function measureLap(track: HTMLElement) {
  const items = track.children;
  const half = items.length / 2;

  if (half < 1) return 0;

  const first = items[0] as HTMLElement;
  const duplicate = items[half] as HTMLElement;

  return Math.abs(duplicate.offsetLeft - first.offsetLeft);
}

/**
 * Drives a horizontally scrollable track as a constant-speed marquee.
 *
 * Swiper's autoplay advances one slide at a time, so it steps rather than
 * drifts, and pausing it mid-transition snaps to the next slide. Nudging a
 * plain overflow container every frame instead gives genuinely continuous
 * motion, freezes exactly where the pointer lands, and leaves the browser's
 * own wheel and touch scrolling working underneath.
 *
 * The caller renders its items twice; the hook wraps by one pass so the seam
 * never shows.
 */
export function useMarqueeScroll({ rtl, resetKey }: MarqueeScrollOptions) {
  const trackRef = useRef<HTMLDivElement>(null);
  const pausedRef = useRef(false);
  const hoveringRef = useRef(false);
  const dragRef = useRef<Drag | null>(null);

  /** The drift stops while the pointer rests on the strip or is dragging it. */
  const sync = useCallback(() => {
    pausedRef.current = hoveringRef.current || dragRef.current !== null;
  }, []);

  const hold = useCallback(() => {
    hoveringRef.current = true;
    sync();
  }, [sync]);

  const release = useCallback(() => {
    hoveringRef.current = false;
    sync();
  }, [sync]);

  const startDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      // Touch and pen already scroll the container natively, and hijacking
      // them would fight the browser's own momentum.
      if (event.pointerType !== "mouse") return;

      const track = trackRef.current;

      if (!track) return;

      // Keeps the drag from selecting the card text it passes over.
      event.preventDefault();

      dragRef.current = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startScroll: track.scrollLeft,
      };
      track.setPointerCapture(event.pointerId);
      sync();
    },
    [sync],
  );

  const moveDrag = useCallback((event: React.PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    const track = trackRef.current;

    if (!drag || !track || drag.pointerId !== event.pointerId) return;

    track.scrollLeft = drag.startScroll - (event.clientX - drag.startX);
  }, []);

  const endDrag = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      const track = trackRef.current;

      if (track?.hasPointerCapture(event.pointerId)) {
        track.releasePointerCapture(event.pointerId);
      }

      dragRef.current = null;
      sync();
    },
    [sync],
  );

  useEffect(() => {
    const track = trackRef.current;

    if (!track) return;

    track.scrollLeft = 0;

    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;

    let lap = measureLap(track);
    let position = 0;
    let lastTime = 0;

    // Card widths are percentage based, so a viewport resize moves the seam.
    const observer = new ResizeObserver(() => {
      lap = measureLap(track);
    });

    observer.observe(track);

    const direction = rtl ? -1 : 1;

    let frame = requestAnimationFrame(function step(time) {
      frame = requestAnimationFrame(step);

      // Capped because frames stop while the browser tab is hidden: without
      // it the first frame back carries the whole absence and the strip leaps.
      const elapsed = lastTime ? Math.min(time - lastTime, MAX_FRAME_MS) : 0;

      lastTime = time;

      // A wheel, a drag or a keyboard scroll moved the track — carry on from
      // wherever it now sits rather than yanking it back.
      if (Math.abs(track.scrollLeft - position) > 1) {
        position = track.scrollLeft;
      }

      if (!pausedRef.current) {
        position += (direction * PIXELS_PER_SECOND * elapsed) / 1000;
      }

      if (lap > 0 && Math.abs(position) >= lap) {
        const shift = position >= lap ? -lap : lap;

        position += shift;

        // A drag in flight measures from where the scroll was when it started,
        // so the wrap has to move that origin with it.
        if (dragRef.current) dragRef.current.startScroll += shift;
      }

      track.scrollLeft = position;
    });

    return () => {
      cancelAnimationFrame(frame);
      observer.disconnect();
    };
  }, [rtl, resetKey]);

  return {
    trackRef,
    trackProps: {
      onMouseEnter: hold,
      onMouseLeave: release,
      onFocusCapture: hold,
      onBlurCapture: release,
      onTouchStart: hold,
      onTouchEnd: release,
      onPointerDown: startDrag,
      onPointerMove: moveDrag,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
    },
  };
}
